import { Component, DoCheck, Input, KeyValueDiffer, KeyValueDiffers, OnInit } from '@angular/core';
import { EnvironmentVariablesService } from '@kenv';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { CardEventType, CardRequestType, CardScreen, Product } from '@ktypes/enums';
import { CardEvent, CardItem } from '@ktypes/models';

@Component({
  selector: 'kp-card-actions',
  templateUrl: './card-actions.component.html',
  styleUrls: ['./card-actions.component.scss'],
})
export class CardActionsComponent implements OnInit, DoCheck {
  @Input() card: CardItem;
  @Input() screen: CardScreen;
  @Input() requestType: CardRequestType;

  isMedia = false;
  isAudio = false;
  isPurposeful = true;
  isVideo = false;
  returnUrl: string;

  private _userStateDiffer: KeyValueDiffer<string, any>;
  private _saveStateDiffer: KeyValueDiffer<string, any>;

  constructor(
    private _cardBloc: CardBloc,
    private _dialogueBloc: DialogueBloc,
    private _differs: KeyValueDiffers,
    private _environmentVariablesService: EnvironmentVariablesService
  ) {}

  ngOnInit() {
    this._userStateDiffer = this._differs.find(this.card?.userState ?? {}).create();
    this._saveStateDiffer = this._differs.find(this.card?.userState?.saveState ?? {}).create();
    this.isPurposeful = this._environmentVariablesService.product === Product.purposeful;
    this.returnUrl = this._cardBloc.getReturnUrlFromCardScreen(this.screen);
  }

  // Changing to doCheck due to changes in card userState not being detected by component
  ngDoCheck() {
    const userStateChanges = this._userStateDiffer?.diff(this.card?.userState);
    const saveStateChanges = this._saveStateDiffer?.diff(this.card?.userState?.saveState);
    if (userStateChanges || saveStateChanges) {
      this._update();
    }
  }

  playMedia(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isAudio) {
      this._cardBloc.detailViewClicked.next({
        detailViewClicked: true,
        card: this.card,
        screen: this.screen,
        audio: true,
      });
    } else if (this.isVideo) {
      this._cardBloc.detailViewClicked.next({
        detailViewClicked: true,
        card: this.card,
        screen: this.screen,
        video: true,
      });
    }
  }

  completeAction(event) {
    event.preventDefault();
    event.stopPropagation();
    void this._cardBloc.handleCardEvents(
      new CardEvent(this.card.id, this.card, CardEventType.COMPLETE, this.requestType),
      null,
      false
    );
  }

  unCompleteAction(event) {
    event.preventDefault();
    event.stopPropagation();
    void this._cardBloc.handleCardEvents(
      new CardEvent(this.card.id, this.card, CardEventType.UNCOMPLETE, this.requestType),
      null,
      false
    );
    this._dialogueBloc.getDialogueStatus(DialogueLogicKey.focus_aware_daily_checkin_dialogue);
  }

  linkClicked(event) {
    event.stopPropagation();
  }

  private _update() {
    this._calculateIsMedia();
    this._calculateIsAudio();
    this._calculateIsVideo();
  }

  private _calculateIsMedia() {
    this.isMedia =
      this.card?.primaryMedia &&
      ['video/mp4', 'audio/mpeg', 'audio/mp3'].indexOf(this.card?.primaryMedia?.contentType) > -1;
  }

  private _calculateIsAudio() {
    this.isAudio =
      this.card?.primaryMedia && ['audio/mpeg', 'audio/mp3'].indexOf(this.card?.primaryMedia?.contentType) > -1;
  }

  private _calculateIsVideo() {
    this.isVideo = this.card?.primaryMedia && ['video/mp4'].indexOf(this.card?.primaryMedia?.contentType) > -1;
  }
}
